import { type Channel } from '@commercetools/platform-sdk'
import { useFetcher, useLoaderData } from '@remix-run/react'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import SearchInput from '#app/components/common/forms/search-input'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import { Button } from '#app/components/ui/button'
import {
	Sheet,
	SheetTrigger,
	SheetContent,
	SheetFooter,
} from '#app/components/ui/sheet'
import { municipalitiesDK } from '#app/constants/municipalities-dk'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader as pdpLoader } from '#app/routes/_public+/product+/$styleNumber'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { cn } from '#app/utils/misc'
import GoogleMapStoresV2Component from './google-maps-stores-v2'
import StoreListForm from './store-list-form'
import { filterChannelsOnAvailability } from './store-selector-drawer'

function getInitialState(selectedStores: any[]) {
	return selectedStores.length > 0 ? selectedStores : []
}

export const StoreSelectorSheet = ({
	selectedStores,
}: {
	selectedStores: any[]
}) => {
	const [query, setQuery] = useState('')
	const [open, setOpen] = useState(false)
	const [selectedStoresState, setSelectedStores] = useState(
		getInitialState(selectedStores),
	)
	const { trackAmplitudeEvent } = useAmplitudeContext()

	let { t } = useTranslation('product_list_page')

	const addStoresFetcher = useFetcher()

	const data = useLoaderData<typeof loader | typeof pdpLoader>()
	const storesChannels = filterChannelsOnAvailability(
		data.storesChannels,
		data.productData?.productInfo,
	)
	// Perform fuzzy search using useMemo to memoize results
	const filteredChannels = useMemo(() => {
		if (query.trim() === '') {
			return storesChannels // If query is empty, return all channels
		}

		const result = storesChannels.filter(channel => {
			// Normalize query for case-insensitive search
			const normalizedQuery = query.toLowerCase()
			if (normalizedQuery.length === 4 && !isNaN(Number(normalizedQuery))) {
				// Check if the query is a postal code
				return channel.address?.postalCode?.includes(normalizedQuery)
			}
			// Check if the channel name, postal code, or municipality matches the query
			const nameMatches = channel.name?.en
				?.toLowerCase()
				.includes(normalizedQuery)
			const postalCodeMatches = channel.address?.postalCode
				?.toLowerCase()
				.includes(normalizedQuery)

			const municipalitiesMatch = municipalitiesDK
				.filter(municipality =>
					municipality.name.toLowerCase().includes(normalizedQuery),
				)
				.find(municipality =>
					municipality.postalCodes.includes(channel.address?.postalCode ?? ''),
				)

			return nameMatches || postalCodeMatches || municipalitiesMatch
		})

		return result
	}, [query, storesChannels])

	const municipalities = useMemo(
		() => groupByMunicipality(filteredChannels),
		[filteredChannels],
	)
	const locations = useMemo(
		() => getLocations(filteredChannels),
		[filteredChannels],
	)

	const handleSelectStores = () => {
		const formData = new FormData()
		formData.append('selectedStores', JSON.stringify(selectedStoresState))
		trackAmplitudeEvent('select stores', {
			selectedStores: selectedStoresState,
		})
		addStoresFetcher.submit(formData, {
			method: 'POST',
			action: '/resources/addSelectedStores',
		})
		setOpen(false)
	}

	const handleMarkerClick = (store: any, selectedStoresKey: string[]) => {
		const markerStore = filteredChannels.find(st => st.key === store)
		if (markerStore && !selectedStoresKey.includes(markerStore.key)) {
			setSelectedStores(prevSelectedStores => [
				...prevSelectedStores,
				{
					name: markerStore.name?.en,
					key: markerStore.key,
				},
			])
		}
	}

	const handleSearch = (e: any) => {
		setQuery(e.target.value)
	}

	useEffect(() => {
		if (open) {
			document.body.classList.add('no-scroll')
		} else {
			document.body.classList.remove('no-scroll')
		}

		return () => {
			document.body.classList.remove('no-scroll')
		}
	}, [open])

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger
				className={cn(
					'flex cursor-pointer flex-row items-center justify-end gap-x-1 md:justify-normal',
					selectedStores.length > 0 ? 'justify-start' : 'justify-end',
				)}
				onClickCapture={() => trackAmplitudeEvent('open select stores sheet')}
			>
				<PlentyBody className="underline">
					{selectedStores.length > 0
						? t('store_selector_sheet.selected_store_trigger')
						: t('store_selector_sheet.no_store_trigger')}
				</PlentyBody>
			</SheetTrigger>
			<SheetContent className="flex h-dvh flex-col gap-y-6">
				<div className="flex flex-col gap-y-2">
					<PlentyHeading as="h4">
						{selectedStores.length > 0
							? t('store_selector_sheet.heading_selected_store')
							: t('store_selector_sheet.heading_no_store')}
					</PlentyHeading>
					<div className="pt-2">
						<SearchInput
							placeholder={t('store_selector_sheet.search_placeholder')}
							onChange={handleSearch}
						/>
					</div>
				</div>
				<GoogleMapStoresV2Component
					locations={locations}
					zoom={6}
					onMarkerClick={handleMarkerClick}
					selectedStoreChannelKey={selectedStoresState.map(store => store.key)}
					height={'400px'}
				/>

				<div
					className="flex w-full grow overflow-y-auto"
					style={{ marginTop: 0 }}
				>
					<StoreListForm
						municipalities={municipalities}
						selectedStores={selectedStoresState}
						setSelectedStores={setSelectedStores}
					/>
				</div>
				<SheetFooter className="flex flex-col justify-end">
					<Button
						variant="primary"
						size="primary"
						onClick={handleSelectStores}
						disabled={selectedStoresState.length === 0}
						fullWidth
					>
						{t('store_selector_sheet.show_button')}
					</Button>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	)
}

/* Helper functions */

export const groupByMunicipality = (storesChannels: Channel[]) => {
	return storesChannels.reduce((acc: any, store: Channel) => {
		const municipality = municipalitiesDK.find(municipality =>
			municipality.postalCodes.includes(store.address?.postalCode ?? ''),
		)?.name

		if (municipality) {
			if (!acc[municipality]) {
				acc[municipality] = []
			}
			acc[municipality].push(store)
		}

		return acc
	}, {})
}

export const getLocations = (storesChannels: Channel[]) =>
	storesChannels
		.filter((store: Channel) => store.geoLocation?.coordinates)
		.map((store: Channel) => ({
			lng: store.geoLocation?.coordinates[0] || 0,
			lat: store.geoLocation?.coordinates[1] || 0, // Ensure correct coordinates (lat, lng)
			storeName: store.name?.en as string,
			storeId: store.name?.en.toLowerCase().replaceAll(' ', '_') as string,
			storeChannelKey: store.key as string,
		}))
